import ReactMarkdown, { type Components } from 'react-markdown';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { sanitizeTalkTrackMarkdown } from '@/cockpit/talk-track-markdown';

// Leadflow's call-prep base. The cockpit links here for general resources and for
// the per-client talk-track page (the inline-render fallback). Defined here so the
// route + this panel share one source (cockpit.tsx re-exports/imports it).
export const LEADFLOW_RESOURCES_URL = 'https://leadflow.throxy.com/resources';

// The leadflow talk-track page for a client, by throxy_client_id — the fallback the
// SDR opens (authed, in a new tab) when the inline track hasn't arrived / has no
// content. Mirrors leadflow's /resources/talk-tracks/clients/<throxyClientId> route.
function talkTrackPageUrl(throxyClientId: string): string {
  return `${LEADFLOW_RESOURCES_URL}/talk-tracks/clients/${encodeURIComponent(throxyClientId)}`;
}

// Minimal markdown styling (no @tailwindcss/typography in this app): map the
// elements a talk track actually uses to readable, compact styles. Links open in a
// new tab — the content is trusted internal markdown and react-markdown renders no
// raw HTML, so there's no XSS surface here.
const MARKDOWN_COMPONENTS: Components = {
  a: ({ node: _node, ...props }) => (
    <a
      {...props}
      target="_blank"
      rel="noopener noreferrer"
      className="text-primary font-medium underline-offset-4 hover:underline"
    />
  ),
  h1: ({ node: _node, ...props }) => <h1 {...props} className="mt-4 mb-2 text-lg font-semibold" />,
  h2: ({ node: _node, ...props }) => (
    <h2 {...props} className="mt-4 mb-2 text-base font-semibold" />
  ),
  h3: ({ node: _node, ...props }) => <h3 {...props} className="mt-3 mb-1 text-sm font-semibold" />,
  p: ({ node: _node, ...props }) => <p {...props} className="my-2 text-sm leading-relaxed" />,
  ul: ({ node: _node, ...props }) => (
    <ul {...props} className="my-2 list-disc space-y-1 pl-5 text-sm" />
  ),
  ol: ({ node: _node, ...props }) => (
    <ol {...props} className="my-2 list-decimal space-y-1 pl-5 text-sm" />
  ),
  li: ({ node: _node, ...props }) => <li {...props} className="leading-relaxed" />,
  strong: ({ node: _node, ...props }) => <strong {...props} className="font-semibold" />,
  blockquote: ({ node: _node, ...props }) => (
    <blockquote {...props} className="text-muted-foreground border-l-2 pl-3 italic" />
  ),
  code: ({ node: _node, ...props }) => (
    <code {...props} className="bg-muted rounded px-1 py-0.5 text-xs" />
  ),
};

// The connected lead's client talk track (call dialogue), shown during a live
// call. `markdown` is the rendered dialogue (null until the separate talk_track
// frame lands — see cockpit/state.ts); `name` is its title; `throxyClientId` drives
// the "open in leadflow" fallback. Renders nothing when there's no client at all.
export function TalkTrackPanel({
  name,
  markdown,
  throxyClientId,
}: {
  name: string | null;
  markdown: string | null;
  throxyClientId: string | null;
}) {
  // No client and no content → nothing to show (e.g. a contact with no client).
  if (!throxyClientId && !markdown) return null;

  return (
    <Card data-testid="talk-track">
      <CardHeader>
        <CardTitle className="text-base">{name ?? 'Talk track'}</CardTitle>
      </CardHeader>
      <CardContent>
        {throxyClientId && (
          <>
            {' '}
            <a
              href={talkTrackPageUrl(throxyClientId)}
              target="_blank"
              rel="noopener noreferrer"
              className="text-primary font-medium underline-offset-4 hover:underline"
            >
              Open in leadflow ↗
            </a>
          </>
        )}

        {markdown ? (
          <div className="max-h-96 overflow-y-auto pr-1" data-testid="talk-track-body">
            <ReactMarkdown components={MARKDOWN_COMPONENTS}>
              {sanitizeTalkTrackMarkdown(markdown)}
            </ReactMarkdown>
          </div>
        ) : (
          <div className="text-muted-foreground text-sm" data-testid="talk-track-fallback">
            Loading talk track…
          </div>
        )}
      </CardContent>
    </Card>
  );
}
